import { ApiError, RateLimitError, UnauthorizedError } from './errors';
import { isMutation, newIdempotencyKey } from './idempotency';
import {
  extractRetryAfterSeconds,
  getCooldownSeconds,
  normalizeSeconds,
  requestKeyOf,
  setCooldown
} from './rateLimit';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getRefreshTransport,
  setTokens
} from './tokenStore';
import { toUnixSeconds } from './types';
import type { ApiEnvelope, ClientConfig, HttpMethod, RefreshResult, RequestOptions } from './types';

/**
 * The Novix HTTP client.
 *
 * Ported from WalletFront's `src/api/client.js`, keeping the two things that
 * were genuinely battle-tested there:
 *
 *   1. SINGLE-FLIGHT TOKEN REFRESH — when N requests race into a 401 at once,
 *      exactly one refresh call goes out and all N await the same promise. The
 *      naive version fires N refreshes, and with rotating refresh tokens N-1 of
 *      them invalidate the session.
 *   2. PER-ENDPOINT RATE-LIMIT COOLDOWNS — see ./rateLimit.ts.
 *
 * Everything else is rewritten: `fetch` instead of axios, typed throughout, no
 * user-facing copy in the transport, no tokens in localStorage.
 */

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_REFRESH_PATH = '/api/v1/auth/refresh';

/**
 * The backend revoked the whole refresh-token family because an already-rotated
 * token was replayed. Distinct from an ordinary expiry: the user has to be told
 * they were signed out *for security*, not just that time passed.
 */
const REFRESH_REUSE_CODE = 'refresh_token_reuse';

let config: Required<ClientConfig> = {
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  timeoutMs: DEFAULT_TIMEOUT_MS,
  refreshPath: DEFAULT_REFRESH_PATH
};

export function configureApiClient(next: Partial<ClientConfig>): void {
  config = { ...config, ...next };
}

export function getApiConfig(): Readonly<Required<ClientConfig>> {
  return config;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = config.baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${normalizedPath}`;

  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      params.append(key, String(value));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  return url;
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (response.status === 204 || !contentType.includes('json')) {
    const text = await response.text().catch(() => '');
    return text === '' ? null : text;
  }
  try {
    return (await response.json()) as unknown;
  } catch (cause) {
    throw new ApiError({ kind: 'parse', status: response.status, cause });
  }
}

function isEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return typeof value === 'object' && value !== null && 'isSuccess' in value;
}

function envelopeError(body: unknown): { errorCode: string | null; detail: string | null } {
  if (!isEnvelope(body)) return { errorCode: null, detail: null };
  return { errorCode: body.errorCode ?? null, detail: body.error ?? null };
}

/** Raises a RateLimitError and records the cooldown for that endpoint. */
function raiseRateLimit(args: {
  method: string;
  path: string;
  requestKey: string;
  headers?: Headers | null;
  body?: unknown;
  fallbackSeconds?: number;
}): never {
  const fromResponse = extractRetryAfterSeconds(args.headers, args.body);
  const retryAfterSeconds =
    fromResponse > 0 ? fromResponse : normalizeSeconds(args.fallbackSeconds ?? 0);

  setCooldown(args.requestKey, retryAfterSeconds);

  const { errorCode, detail } = envelopeError(args.body);
  throw new RateLimitError({
    method: args.method,
    path: args.path,
    requestKey: args.requestKey,
    retryAfterSeconds,
    errorCode,
    detail
  });
}

/** `AbortSignal.any` where available; a manual bridge in older environments. */
function combineSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([a, b]);

  const controller = new AbortController();
  const abort = () => {
    controller.abort();
  };
  if (a.aborted || b.aborted) abort();
  a.addEventListener('abort', abort, { once: true });
  b.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

// ---------------------------------------------------------------------------
// Single-flight refresh
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

/** Exposed for tests only. */
export function __resetRefreshState(): void {
  refreshInFlight = null;
}

async function performRefresh(): Promise<string> {
  const usesCookie = getRefreshTransport() === 'cookie';
  const refreshToken = getRefreshToken();

  if (!usesCookie && !refreshToken) {
    clearTokens('no-refresh-token');
    throw new UnauthorizedError({ path: config.refreshPath, method: 'POST' });
  }

  const requestKey = requestKeyOf('POST', config.refreshPath);
  const cooldown = getCooldownSeconds(requestKey);
  if (cooldown > 0) {
    // The refresh endpoint itself is rate-limited: the session is unusable, so
    // end it rather than leaving the app in a half-authenticated state.
    clearTokens('refresh-failed');
    raiseRateLimit({
      method: 'POST',
      path: config.refreshPath,
      requestKey,
      fallbackSeconds: cooldown
    });
  }

  const response = await fetch(buildUrl(config.refreshPath), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: usesCookie ? 'include' : 'same-origin',
    body: JSON.stringify(usesCookie ? {} : { refreshToken })
  });

  const body = await readBody(response);

  if (response.status === 429) {
    clearTokens('refresh-failed');
    raiseRateLimit({
      method: 'POST',
      path: config.refreshPath,
      requestKey,
      headers: response.headers,
      body
    });
  }

  const envelope = isEnvelope(body) ? (body as ApiEnvelope<RefreshResult>) : null;
  const result = envelope?.result ?? null;
  const accessToken = result?.accessToken;

  if (!response.ok || envelope?.isSuccess !== true || !accessToken) {
    const { errorCode, detail } = envelopeError(body);
    clearTokens(errorCode === REFRESH_REUSE_CODE ? 'session-revoked' : 'refresh-failed');
    throw new UnauthorizedError({
      status: response.status,
      method: 'POST',
      path: config.refreshPath,
      errorCode,
      detail
    });
  }

  setTokens({
    accessToken,
    refreshToken: result.newRefreshToken ?? result.refreshToken ?? getRefreshToken(),
    expiresAt: toUnixSeconds(result.expiresAt ?? result.accessTokenExpiresAt)
  });

  return accessToken;
}

/**
 * Refreshes the access token, collapsing concurrent callers onto one request.
 * The in-flight promise is cleared in `finally` so a later 401 can retry.
 */
export function refreshAccessToken(): Promise<string> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

async function execute<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions,
  isRetry: boolean
): Promise<T> {
  const requestKey = requestKeyOf(method, path);

  const localCooldown = getCooldownSeconds(requestKey);
  if (localCooldown > 0) {
    raiseRateLimit({ method, path, requestKey, fallbackSeconds: localCooldown });
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers
  };

  const hasBody = options.body !== undefined && method !== 'GET';
  if (hasBody) headers['Content-Type'] = 'application/json';

  if (isMutation(method)) {
    // Stable across our own 401-retry: the key is generated once per call site
    // and reused when we replay the request after a refresh.
    headers['Idempotency-Key'] = options.idempotencyKey ?? newIdempotencyKey();
  }

  if (!options.skipAuth) {
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const timeoutMs = options.timeoutMs ?? config.timeoutMs;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    timeoutController.abort();
  }, timeoutMs);
  const signal = options.signal
    ? combineSignals(options.signal, timeoutController.signal)
    : timeoutController.signal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: getRefreshTransport() === 'cookie' ? 'include' : 'same-origin',
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      signal
    });
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    const kind = timeoutController.signal.aborted ? 'timeout' : 'network';
    throw new ApiError({ kind, method, path, cause });
  } finally {
    clearTimeout(timeoutId);
  }

  const body = await readBody(response);

  if (response.status === 429) {
    raiseRateLimit({ method, path, requestKey, headers: response.headers, body });
  }

  if (response.status === 401 && !options.skipAuth && !options.skipRefresh && !isRetry) {
    // One refresh, one replay. `isRetry` stops an infinite 401 loop when the
    // freshly minted token is also rejected.
    await refreshAccessToken();
    return execute<T>(method, path, options, true);
  }

  const envelope = isEnvelope(body) ? (body as ApiEnvelope<T>) : null;

  if (!response.ok || (envelope !== null && envelope.isSuccess !== true)) {
    const { errorCode, detail } = envelopeError(body);
    if (response.status === 401) {
      throw new UnauthorizedError({ method, path, errorCode, detail });
    }
    throw new ApiError({ kind: 'http', status: response.status, method, path, errorCode, detail });
  }

  if (envelope === null) {
    // Endpoint answered outside the envelope (or 204). Pass the payload through.
    return body as T;
  }

  return envelope.result as T;
}

export const apiClient = {
  get: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    execute<T>('GET', path, options, false),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    execute<T>('POST', path, { ...options, body }, false),
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    execute<T>('PUT', path, { ...options, body }, false),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    execute<T>('PATCH', path, { ...options, body }, false),
  delete: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    execute<T>('DELETE', path, options, false)
};

export type ApiClient = typeof apiClient;
